import React, { useEffect, useReducer, useMemo, useState } from "react";
import classes from "./CreateRoutineTemplate.scss";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import classNames from "classnames";

import {
  Button,
  Checkbox,
  Dropdown,
  RadioButton,
  TextAreaInput,
  TextInput,
  DropdownMenu,
  ButtonDisclosure,
  DSProvider,
  List,
  Avatar,
  Dialog,
  Tooltip,
  IconButton,
} from "@toddle-edu/ds-web";
import {
  AddOutlined,
  DeleteOutlined,
  DownArrowOutlined,
  PencilLineOutlined,
  UpArrowOutlined,
} from "@toddle-edu/ds-icons";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import Loading from "UIComponents/Loading";

import { getSelectedCurriculumId } from "modules/CommonSelectors";
import ScreenHeader from "../ScreenHeader/ScreenHeader";
import TimeRangePicker from "../TimeRange";
import RightPane from "UIComponents/RightPane/RightPane";
import RightPaneContent from "../RightPaneContent";
import Attachments from "../Attachments";
import MealField from "../MealField";
import Stepper from "../Stepper";
import {
  ADD_NEW_FIELD,
  DELETE_FIELD,
  DELETE_MEAL_MENU,
  deleteRoutineTemplate,
  REPLACE_FIELD,
  RESET_TEMPLATE,
  routineTemplatesReducer,
  SWAP_FEILDS,
  UPDATE_MEAL_MENUS,
  UPDATE_TEMPLATE_FIELDS,
  updateClasses,
} from "../../modules/RoutineTemplatesModule";
import {
  getCoursesFormatted,
  getCreateTemplateData,
  getFieldDropdownOptions,
  getUpdateTemplateData,
  transformFieldDataSets,
} from "../../utils";

const styles = {
  addButtonStyles: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    columnGap: "8px",
  },
  selectedFieldCard: {
    border: "1px solid var(--border-focus)",
  },
  rightPaneContainerStyle: {
    width: "376px",
    top: "54px",
    marginRight: "24px",
  },
  rightPaneMiddleContainer: {
    padding: "0px",
  },
  rightPaneHeaderStyle: {
    height: "52px",
    padding: "12px 16px",
    paddingRight: "8px",
  },
  fullWidthStyle: { width: "616px" },
  mapClassButtonStyle: { width: "130px" },
  dividerStyle: {
    borderBottom: "1px solid var(--border-secondary)",
  },
  dropdownMenuStyle: { minHeight: "325px" },
  infoTooltipStyle: {
    width: "280px",
  },
};

const addIcon = <AddOutlined />;
const BLANK_OBJECT = {};
const BLANK_ARRAY = [];

export const TEMPLATE_STATES = {
  ARCHIVED: "ARCHIVED",
  PUBLISHED: "PUBLISHED",
  DRAFT: "DRAFT",
};

export const FIELD_TYPE_ENUM = {
  SHORT_ANSWER: "SHORT_ANSWER",
  PARAGRAPH: "PARAGRAPH",
  SINGLE_SELECT: "SINGLE_SELECT",
  MULTI_SELECT: "MULTI_SELECT",
  DROPDOWN: "DROPDOWN",
  STEPCOUNT: "STEPCOUNT",
  TIME: "TIME",
  TIME_RANGE: "TIME_RANGE",
  MEAL: "MEAL",
  ATTACHMENT: "ATTACHMENT",
  SECTION_HEADER: "SECTION_HEADER",
};

export const CreateRoutineTemplate = props => {
  const {
    isLoading,
    closeCreateTemplate,
    curriculumProgramId,
    templateData,
    createPostRoutineTemplate,
    deleteRoutineTemplate,
    updateRoutineTemplate,
    resetState,
    toggleClassMappingModal,
    mappedClasses,
    updateClasses,
    isAllClassesSelected,
    isTemplateModified,
    setIsTemplateModified,
  } = props;
  const { t } = useTranslation(["common", "journal"]);
  const [templateTitle, setTemplateTile] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedField, setSelectedField] = useState("");
  const [showRightPane, setShowRightPane] = useState(false);
  const [viewAllClasses, setViewAllClasses] = useState(false);
  const [showFullScreenLoader, setShowFillScreenLoader] = useState(false);
  const [mode, setMode] = useState("CREATE");
  const triggerTypes = ["click"];
  const classDisplayCount = 3;
  const openClassMappingModal = () => toggleClassMappingModal(true);

  const defaultLables = {
    [FIELD_TYPE_ENUM.TIME]: t("journal:label_of_time_selector"),
    [FIELD_TYPE_ENUM.TIME_RANGE]: t("journal:label_of_time_range"),
    [FIELD_TYPE_ENUM.SINGLE_SELECT]: t("journal:label_of_single_select"),
    [FIELD_TYPE_ENUM.MULTI_SELECT]: t("journal:label_of_multi_select"),
    [FIELD_TYPE_ENUM.DROPDOWN]: t("journal:label_of_dropdown"),
    [FIELD_TYPE_ENUM.PARAGRAPH]: t("journal:label_of_text_area"),
    [FIELD_TYPE_ENUM.SHORT_ANSWER]: t("journal:enter_question_here"),
    [FIELD_TYPE_ENUM.ATTACHMENT]: t("common:attachment"),
    [FIELD_TYPE_ENUM.STEPCOUNT]: t("journal:label_of_setpcount"),
    [FIELD_TYPE_ENUM.SECTION_HEADER]: t("journal:untitled_section"),
  };

  const fieldListItems = useMemo(() => {
    return getFieldDropdownOptions({ t });
  }, [t]);

  const {
    templateFields,
    templateId,
    entityMap,
    title,
    selectedTemplate,
    isNewTemplatePresent,
    headerData,
  } = useMemo(() => {
    const selectedTemplate = _.get(templateData, "isNewTemplateRequired", false)
      ? _.get(templateData, "newTemplate")
      : _.get(templateData, "oldTemplate");
    const isNewTemplatePresent = !_.isEmpty(_.get(templateData, "newTemplate"));
    const templateFields = _.get(selectedTemplate, "fields");
    const templateId = _.get(selectedTemplate, "id");
    const entityMap = _.get(selectedTemplate, "entityMap");
    const title = _.get(selectedTemplate, "title");
    const updatedByName = !_.isEmpty(_.get(selectedTemplate, "updatedBy"))
      ? `${_.get(selectedTemplate, "updatedBy.firstName")} ${_.get(
          selectedTemplate,
          "updatedBy.lastName"
        )}`
      : null;
    const taggedClassNames = _.map(mappedClasses, entity =>
      _.get(entity, "node.title")
    );
    return {
      templateFields,
      templateId,
      entityMap,
      title,
      selectedTemplate,
      isNewTemplatePresent,
      headerData: { title, taggedClassNames, updatedByName },
    };
  }, [templateData, mappedClasses]);

  const [fieldsData, dispatch] = useReducer(routineTemplatesReducer, {
    templateFields: !_.isEmpty(templateFields) ? templateFields : [],
    mealMenus: [],
    isEditMode: false,
  });

  const { templateFields: fields, isEditMode, mealMenus } = fieldsData;

  const updateMealMenus = (menus, fieldId) => {
    dispatch({
      type: UPDATE_MEAL_MENUS,
      payload: {
        id: fieldId,
        menus,
      },
    });
  };

  useEffect(() => {
    if (!_.isEmpty(selectedTemplate)) {
      dispatch({
        type: UPDATE_TEMPLATE_FIELDS,
        payload: {
          templateFields,
        },
      });
    }
    setTemplateTile(title);
  }, [selectedTemplate]);

  useEffect(() => {
    if (isEditMode) {
      const coursesTagged = getCoursesFormatted(entityMap);
      updateClasses?.({ classes: coursesTagged });
    }
  }, [entityMap, isEditMode, updateClasses]);

  const getHasDataSet = field => {
    return _.includes(
      [
        FIELD_TYPE_ENUM.SINGLE_SELECT,
        FIELD_TYPE_ENUM.MULTI_SELECT,
        FIELD_TYPE_ENUM.DROPDOWN,
      ],
      _.get(field, "type")
    );
  };

  const getTransformedOptions = _.memoize(options => {
    return _.map(_.sortBy(options, "displaySequence"), option => {
      return {
        key: _.get(option, "displaySequence"),
        label: _.get(option, "value"),
      };
    });
  }, JSON.stringify);

  const refetchTemplatesAfterDelete = () => {
    closeCreateTemplate({ refetch: "false", reset: "false" });
  };

  const onDiscardAndExit = () => {
    if (isNewTemplatePresent) {
      deleteRoutineTemplate?.({
        ids: [templateId],
        hideToast: true,
        refetchTemplates: refetchTemplatesAfterDelete,
      });
    } else {
      closeCreateTemplate({ refetch: "false", reset: "false" });
    }
  };

  const archiveOldTemplate = () => {
    const oldTemplateId = _.get(templateData, "oldTemplate.id");
    const isNewTemplateRequired = _.get(
      templateData,
      "isNewTemplateRequired",
      false
    );
    if (isNewTemplateRequired) {
      updateRoutineTemplate({
        isNewTemplateRequired,
        templateMetaData: {
          id: oldTemplateId,
          state: TEMPLATE_STATES.ARCHIVED,
        },
      });
    }
  };

  const onPublish = async () => {
    setShowFillScreenLoader(true);
    await onClickSave();
  };

  const onClickSave = async () => {
    try {
      if (!isEditMode) {
        const templateData = getCreateTemplateData({
          templateTitle,
          curriculumProgramId,
          isAllClassesSelected,
          mappedClasses,
          fields,
        });

        await createPostRoutineTemplate({
          templateData,
          closeCreateTemplate,
        });
      } else {
        const templateMetaData = getUpdateTemplateData({
          templateId,
          curriculumProgramId,
          isAllClassesSelected,
          mappedClasses,
          entityMap,
          templateTitle,
          title,
        });

        await updateRoutineTemplate({
          templateMetaData,
          oldTemplateData: transformFieldDataSets(templateFields),
          updatedTemplateData: fields,
          closeCreateTemplate,
          archiveOldTemplate,
        });
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setShowFillScreenLoader(false);
    }
  };

  const onAddField = ({
    fieldId,
    previousDisplaySequence,
    fieldType,
    isReplace,
  }) => {
    const updatedFields = _.cloneDeep(fields);
    const displaySequence = isReplace
      ? previousDisplaySequence
      : _.size(updatedFields) + 1;
    let field;
    const initialEntities = {
      type: fieldType,
      id: `${fieldType}-${displaySequence}`,
      displaySequence,
    };
    const fieldDataSet = {
      dataSet: {
        type: "SELECT_OPTION",
        curriculumProgramId,
      },
    };
    setIsTemplateModified(true);
    switch (fieldType) {
      case FIELD_TYPE_ENUM.SECTION_HEADER:
      case FIELD_TYPE_ENUM.SHORT_ANSWER:
      case FIELD_TYPE_ENUM.PARAGRAPH:
      case FIELD_TYPE_ENUM.STEPCOUNT:
      case FIELD_TYPE_ENUM.TIME:
      case FIELD_TYPE_ENUM.TIME_RANGE:
        field = _.cloneDeep(initialEntities);
        updatedFields.push(field);
        break;
      case FIELD_TYPE_ENUM.ATTACHMENT:
        field = _.cloneDeep(initialEntities);
        updatedFields.push({ ...field, label: t("common:attachment") });
        break;
      case FIELD_TYPE_ENUM.SINGLE_SELECT:
        field = {
          ...initialEntities,
          dataSet: {
            type: "SELECT_OPTION",
            curriculumProgramId,
            values: [
              { value: "Option 1", displaySequence: 1 },
              { value: "Option 2", displaySequence: 2 },
            ],
          },
        };
        updatedFields.push(field);
        break;
      case FIELD_TYPE_ENUM.MULTI_SELECT:
        field = {
          ...initialEntities,
          dataSet: {
            type: "SELECT_OPTION",
            curriculumProgramId,
            values: [
              { value: "Option 1", displaySequence: 1 },
              { value: "Option 2", displaySequence: 2 },
            ],
          },
        };
        updatedFields.push(field);
        break;
      case FIELD_TYPE_ENUM.DROPDOWN:
        field = {
          ...initialEntities,
          ...fieldDataSet,
        };
        updatedFields.push(field);
        break;
      case FIELD_TYPE_ENUM.MEAL:
        field = {
          ...initialEntities,
          dataSetIds: [],
          ...fieldDataSet,
        };
        updatedFields.push(field);
        break;

      default:
        break;
    }
    if (isReplace) {
      dispatch({
        type: REPLACE_FIELD,
        payload: {
          fieldId,
          field,
          displaySequence,
        },
      });
      setSelectedField(field);
    } else {
      onSelectField(field)();
      dispatch({
        type: ADD_NEW_FIELD,
        payload: field,
      });
    }
  };

  const renderOptions = field => {
    const options = _.get(field, "dataSet.values", []);
    const isRadioOption =
      _.get(field, "type") === FIELD_TYPE_ENUM.SINGLE_SELECT;
    return (
      <div className={classes.optionsContainer}>
        {_.map(_.sortBy(options, "displaySequence"), option => {
          if (_.isEmpty(_.get(option, "value", "")) && mode === "PREVIEW") {
            return null;
          }
          return (
            <div>
              {isRadioOption ? (
                <RadioButton checked={false} size="medium" name="option">
                  {_.get(option, "value", "")}
                </RadioButton>
              ) : (
                <Checkbox isChecked={false} size="medium" name="option">
                  <span className={classes.checkboxLabel}>
                    {_.get(option, "value", "")}
                  </span>
                </Checkbox>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const stopPropagation = e => {
    e?.stopPropagation();
  };

  const getPopupContainer = el => {
    return el;
  };

  const renderField = field => {
    const fieldType = _.get(field, "type", "");
    switch (fieldType) {
      case FIELD_TYPE_ENUM.SHORT_ANSWER:
        return (
          <TextInput
            testId={`routine-templates-createroutinetemplate-shortanswerfield-${_.get(
              field,
              "id"
            )}`}
            style={styles.fullWidthStyle}
            onClick={stopPropagation}
            placeholder={_.get(field, "placeholder") || t("journal:type_here")}
            size={"medium"}
            value=""
          />
        );
      case FIELD_TYPE_ENUM.PARAGRAPH:
        return (
          <div style={styles.fullWidthStyle}>
            <TextAreaInput
              testId={`routine-templates-createroutinetemplate-paragraphfield-${_.get(
                field,
                "id"
              )}`}
              onClick={stopPropagation}
              placeholder={
                _.get(field, "placeholder") || t("journal:type_here")
              }
              maxRows={3}
              value=""
            />
          </div>
        );
      case FIELD_TYPE_ENUM.SINGLE_SELECT:
        return renderOptions(field);
      case FIELD_TYPE_ENUM.MULTI_SELECT:
        return renderOptions(field);
      case FIELD_TYPE_ENUM.DROPDOWN:
        return (
          <div style={styles.fullWidthStyle}>
            <Dropdown
              className={classes.dropdownContainer}
              getPopupContainer={getPopupContainer}
              overlay={
                <DropdownMenu
                  options={getTransformedOptions(
                    _.get(field, "dataSet.values")
                  )}
                  shouldStopPropagation={true}
                  testId={`routine-templates-createroutinetemplate-dropdownmenufield-${_.get(
                    field,
                    "id"
                  )}`}
                />
              }
              placement={"bottomRight"}
            >
              <ButtonDisclosure
                variant="neutral"
                type="outlined"
                size="medium"
                isFullWidth={true}
              >
                {t("journal:select_an_option")}
              </ButtonDisclosure>
            </Dropdown>
          </div>
        );
      case FIELD_TYPE_ENUM.STEPCOUNT:
        return (
          <Stepper
            testId={`routine-templates-createroutinetemplate-stepperfield-${_.get(
              field,
              "id"
            )}`}
            field={field}
          />
        );
      case FIELD_TYPE_ENUM.TIME:
        return <TimeRangePicker isTimeSelector={true} field={field} />;
      case FIELD_TYPE_ENUM.TIME_RANGE:
        return <TimeRangePicker field={field} />;
      case FIELD_TYPE_ENUM.ATTACHMENT:
        return (
          <Attachments
            testId={`routine-templates-createroutinetemplate-attachmentfield-${_.get(
              field,
              "id"
            )}`}
          />
        );
      case FIELD_TYPE_ENUM.MEAL:
        return (
          <MealField
            isPreviewMode={mode === "PREVIEW"}
            field={field}
            isEditMode={isEditMode}
            mealMenus={mealMenus[_.get(field, "id")] || BLANK_ARRAY}
          />
        );

      default:
        break;
    }
  };

  const onSelectField = field => () => {
    if (mode !== "PREVIEW") {
      setShowRightPane(true);
      setSelectedField(field);
    }
  };

  const onChangeField = params => {
    onAddField({ ...params, isReplace: true });
  };

  const getRightPaneContent = ({ t, selectedField }) => {
    return (
      <RightPaneContent
        t={t}
        templateFields={fields}
        selectedFieldId={_.get(selectedField, "id")}
        mealMenus={mealMenus[_.get(selectedField, "id")] || BLANK_ARRAY}
        dispatch={dispatch}
        onChangeField={onChangeField}
        updateMealMenus={updateMealMenus}
        setIsTemplateModified={setIsTemplateModified}
      />
    );
  };

  const getRightPaneContentElement = ({ content }) => [
    {
      key: "custom_content",
      type: "custom",
      content,
    },
  ];

  const toggleRightPane = e => {
    e?.stopPropagation();
    setShowRightPane(false);
    setSelectedField(null);
  };

  const onDeleteField = field => () => {
    setShowRightPane(false);
    setIsTemplateModified(true);
    if (_.get(field, "type") === FIELD_TYPE_ENUM.MEAL) {
      dispatch({
        type: DELETE_MEAL_MENU,
        payload: {
          fieldId: _.get(field, "id"),
        },
      });
    }
    dispatch({
      type: DELETE_FIELD,
      payload: {
        fieldId: _.get(field, "id"),
      },
    });
  };

  const onSwitchField = (field, move) => () => {
    let updatedDisplaySequence;
    setIsTemplateModified(true);
    if (move === "UP" && _.get(field, "displaySequence") > 1) {
      updatedDisplaySequence = _.get(field, "displaySequence") - 1;
      dispatch({
        type: SWAP_FEILDS,
        payload: {
          id: _.get(field, "id"),
          prevDisplaySequence: _.get(field, "displaySequence"),
          updatedDisplaySequence,
        },
      });
    } else if (
      move === "DOWN" &&
      _.get(field, "displaySequence") < _.size(fields)
    ) {
      updatedDisplaySequence = _.get(field, "displaySequence") + 1;
      dispatch({
        type: SWAP_FEILDS,
        payload: {
          id: _.get(field, "id"),
          prevDisplaySequence: _.get(field, "displaySequence"),
          updatedDisplaySequence,
        },
      });
    }
  };

  const getClassList = ({ mappedClasses }) => {
    return _.map(
      !viewAllClasses
        ? _.slice(mappedClasses, 0, classDisplayCount)
        : mappedClasses,
      classItem => {
        return {
          id: _.get(classItem, "node.id"),
          value: (
            <div className={classes.listItem}>
              <Avatar
                id={_.get(classItem, "node.id")}
                name={_.get(classItem, "node.title")}
                dsVersion="2.0"
                shade="light"
                size="small"
                shape="square"
              />
              <label>{_.get(classItem, "node.title")}</label>
            </div>
          ),
        };
      }
    );
  };

  const changeMode = mode => () => {
    setMode(mode);
    setSelectedField(null);
    if (mode === "PREVIEW") {
      setShowRightPane(false);
    }
  };

  const resetAndCloseCreateModal = () => {
    dispatch({
      type: RESET_TEMPLATE,
      payload: null,
    });
    resetState();
    closeCreateTemplate();
  };

  const onSetViewAllClasses = () => {
    setViewAllClasses(true);
  };

  const hideAllClasses = () => {
    setViewAllClasses(false);
  };

  const getPreviewFieldClasses = ({
    isLastField,
    isAttachment,
    isMiddleSectionHeader,
  }) => {
    return classNames({
      [classes.field]: mode !== "PREVIEW" || isLastField,
      [classes.previewField]: mode === "PREVIEW" && !isLastField,
      [classes.attachMentField]: isAttachment,
      [classes.middleSectionHeader]:
        mode === "PREVIEW" && isMiddleSectionHeader,
    });
  };

  const onChangeTitle = e => {
    setIsTemplateModified(true);
    setTemplateTile(e.target.value);
  };

  const openDiscardDialog = () => {
    if (isEditMode && isTemplateModified) {
      setOpenDialog(true);
    } else {
      closeCreateTemplate();
    }
  };

  const closeDialog = () => setOpenDialog(false);

  const onClickAddField = e => {
    onAddField({
      fieldType: e.key,
    });
  };

  const getLabelClasses = field => {
    if (_.get(field, "type") === FIELD_TYPE_ENUM.SECTION_HEADER) {
      if (mode === "PREVIEW") {
        return "text-label-l";
      } else {
        return classNames({
          [classes.sectionHeaderLabel]: true,
          [classes.sectionHeaderPlaceholder]: _.isEmpty(field.label),
          "text-label-l": true,
        });
      }
    }
    return "text-label";
  };

  const rightPaneData = getRightPaneContentElement({
    content: getRightPaneContent({ t, selectedField }),
  });

  const crossButtonProps = { size: "small", styles: { padding: "0px" } };

  const fieldCardClasses = (type, prevType) => {
    return classNames({
      [classes.fieldCard]: mode !== "PREVIEW",
      [classes.fieldWrapper]: mode === "PREVIEW",
      [classes.sectionHeaderField]:
        mode === "PREVIEW" && type === FIELD_TYPE_ENUM.SECTION_HEADER,
      [classes.previousSectionHeader]:
        mode === "PREVIEW" && prevType === FIELD_TYPE_ENUM.SECTION_HEADER,
    });
  };

  const fieldCardContainerClasses = classNames({
    [classes.fieldContainer]: mode !== "PREVIEW",
  });

  const containerClasses = classNames({
    [classes.container]: true,
    [classes.loading]: isLoading,
  });

  const durationClass = classNames({
    ["text-body"]: true,
    [classes.duration]: true,
  });

  const templateContainerClasses = classNames({
    [classes.templateContent]: true,
    [classes.transform]: showRightPane,
  });

  return (
    <DSProvider dsVersion="2.0">
      <div
        className={containerClasses}
        data-test-id="routine-templates-createroutinetemplate-container"
      >
        <ScreenHeader
          title={templateTitle}
          headerData={headerData}
          isPreview={mode === "PREVIEW"}
          isEditMode={isEditMode}
          changeMode={changeMode}
          onSave={onPublish}
          closeCreateTemplate={resetAndCloseCreateModal}
          openDiscardDialog={openDiscardDialog}
          testId="routine-templates-createroutinetemplate-header"
        />
        {isLoading && isEditMode ? (
          <div className={classes.loaderContainer}>
            <Loading testId="routine-templates-createroutinetemplate-loader" />
          </div>
        ) : null}
        {!isLoading || !isEditMode ? (
          <div
            className={templateContainerClasses}
            data-test-id="routine-templates-createroutinetemplate-content"
          >
            {mode !== "PREVIEW" ? (
              <div
                className={classes.fieldCard}
                data-test-id="routine-templates-createroutinetemplate-fieldcard"
              >
                <div className={classes.field}>
                  <TextInput
                    value={templateTitle}
                    style={styles.fullWidthStyle}
                    name={"name"}
                    label={t("journal:name_of_the_template")}
                    placeholder={t("common:untitled")}
                    size={"medium"}
                    onChange={onChangeTitle}
                    testId="routine-templates-createroutinetemplate-title"
                  />
                </div>
                <div
                  className={classes.classContainer}
                  data-test-id="routine-templates-createroutinetemplate-classcontainer"
                >
                  <div className={classes.mappingContainer}>
                    <label className="text-label">
                      {_.size(mappedClasses) > 0
                        ? t("journal:map_classes_to_templates_count", {
                            count: _.size(mappedClasses),
                          })
                        : t("journal:map_classes_to_templates")}
                    </label>
                    {!_.isEmpty(mappedClasses) ? (
                      <Button
                        variant="neutral"
                        type="outlined"
                        size="medium"
                        icon={<PencilLineOutlined />}
                        onClick={openClassMappingModal}
                        testId="routine-templates-createroutinetemplate-editclasses"
                      >
                        {t("common:edit")}
                      </Button>
                    ) : null}
                  </div>

                  {!_.isEmpty(mappedClasses) ? (
                    <div className={classes.classList}>
                      <List
                        listItemClassName={classes.listItem}
                        data={getClassList({
                          mappedClasses,
                        })}
                        isDraggable={false}
                        testId="routine-templates-createroutinetemplate-classlist"
                      />
                      {_.size(mappedClasses) > classDisplayCount &&
                      !viewAllClasses ? (
                        <Button
                          variant="progressive"
                          type="inline"
                          size="medium"
                          icon={addIcon}
                          onClick={onSetViewAllClasses}
                          testId="routine-templates-createroutinetemplate-viewmore"
                        >
                          {t("journal:view_more_count", {
                            count: _.size(mappedClasses) - classDisplayCount,
                          })}
                        </Button>
                      ) : null}

                      {viewAllClasses ? (
                        <Button
                          variant="progressive"
                          type="inline"
                          size="medium"
                          onClick={hideAllClasses}
                          testId="routine-templates-createroutinetemplate-viewless"
                        >
                          {t("common:show_less")}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                  {_.isEmpty(mappedClasses) ? (
                    <Button
                      style={styles.mapClassButtonStyle}
                      variant="neutral"
                      type="outlined"
                      size="medium"
                      icon={addIcon}
                      onClick={openClassMappingModal}
                      testId="routine-templates-createroutinetemplate-mapclasses"
                    >
                      {t("journal:map_classes")}
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {!_.isEmpty(fields) ? (
              <div
                className={mode === "PREVIEW" ? classes.previewContainer : ""}
                data-test-id="routine-templates-createroutinetemplate-fieldscontainer"
              >
                {_.map(_.sortBy(fields, "displaySequence"), (field, index) => {
                  return (
                    <div key={field.id} className={fieldCardContainerClasses}>
                      <div
                        className={fieldCardClasses(
                          _.get(field, "type"),
                          _.get(fields, [index - 1, "type"])
                        )}
                        style={
                          _.get(selectedField, "id") === _.get(field, "id") &&
                          mode !== "PREVIEW"
                            ? styles.selectedFieldCard
                            : BLANK_OBJECT
                        }
                        data-test-id={`routine-templates-createroutinetemplate-field-${field.id}`}
                        onClick={onSelectField(field)}
                      >
                        <div
                          className={getPreviewFieldClasses({
                            isLastField: index + 1 === _.size(fields),
                            isAttachment:
                              _.get(field, "type") ===
                              FIELD_TYPE_ENUM.ATTACHMENT,
                            isMiddleSectionHeader:
                              _.get(field, "type") ===
                                FIELD_TYPE_ENUM.SECTION_HEADER && index !== 0,
                          })}
                        >
                          {getHasDataSet(field) ? (
                            <label className="text-label">
                              {_.get(
                                field,
                                "dataSet.title",
                                defaultLables[_.get(field, "type")]
                              )}
                            </label>
                          ) : (
                            <label className={getLabelClasses(field)}>
                              {!_.isEmpty(field?.label)
                                ? _.get(
                                    field,
                                    "label",
                                    defaultLables[_.get(field, "type")]
                                  )
                                : defaultLables[_.get(field, "type")]}
                            </label>
                          )}
                          {renderField(field)}
                          {_.get(field, "type") ===
                            FIELD_TYPE_ENUM.TIME_RANGE &&
                          _.get(
                            field,
                            "metadata.autoCalculateTotalDuration"
                          ) ? (
                            <label className={durationClass}>
                              {t("journal:duration_in_hr")}
                            </label>
                          ) : null}
                        </div>
                      </div>
                      {_.get(selectedField, "id") === _.get(field, "id") &&
                      mode !== "PREVIEW" ? (
                        <div className={classes.fieldSwitcher}>
                          <div className={classes.arrowsContainer}>
                            <Tooltip
                              tooltip={t("journal:move_up")}
                              placement="right"
                            >
                              <IconButton
                                type="plain"
                                variant="neutral"
                                onClick={onSwitchField(field, "UP")}
                                disabled={_.get(field, "displaySequence") === 1}
                                icon={<UpArrowOutlined />}
                                testId={`routine-templates-createroutinetemplate-fieldmoveup-${field.id}`}
                              />
                            </Tooltip>
                            <Tooltip
                              showArrow
                              tooltip={t("journal:move_down")}
                              placement="right"
                            >
                              <IconButton
                                type="plain"
                                variant="neutral"
                                onClick={onSwitchField(field, "DOWN")}
                                disabled={
                                  _.get(field, "displaySequence") ===
                                  _.size(fields)
                                }
                                icon={<DownArrowOutlined />}
                                testId={`routine-templates-createroutinetemplate-fieldmovedown-${field.id}`}
                              />
                            </Tooltip>
                          </div>
                          <div className={classes.deleteIcon}>
                            <Tooltip
                              showArrow
                              tooltip={t("common:delete")}
                              placement="right"
                            >
                              <IconButton
                                type="plain"
                                variant="neutral"
                                onClick={onDeleteField(field)}
                                icon={<DeleteOutlined />}
                                testId={`routine-templates-createroutinetemplate-fielddelete-${field.id}`}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {mode !== "PREVIEW" ? (
              <div className={classes.addButtonContainer}>
                <Dropdown
                  overlay={
                    <DropdownMenu
                      testId="routine-templates-createroutinetemplate-addfielddropdownmenu"
                      style={styles.dropdownMenuStyle}
                      options={fieldListItems}
                      onClick={onClickAddField}
                      dsVersion="2.0"
                    />
                  }
                  trigger={triggerTypes}
                  testId="routine-templates-createroutinetemplate-addfielddropdown"
                >
                  <ButtonDisclosure
                    className={classes.dropdownButton}
                    isFullWidth={true}
                    dsVersion="2.0"
                    type="outlined"
                    variant="neutral"
                    shouldStopPropagation
                    icon={addIcon}
                    testId="routine-templates-createroutinetemplate-addfieldbutton"
                  >
                    {t("journal:add_a_field")}
                  </ButtonDisclosure>
                </Dropdown>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {openDialog ? (
        <Dialog
          dsVersion={"2.0"}
          variant="delete-dialog"
          title={t("journal:you_have_unsaved_changes")}
          message={t("journal:all_unsaved_changes_will_be_lost")}
          buttonOneText={t("journal:discard_and_exit")}
          buttonTwoText={t("journal:save_and_exit")}
          onButtonOneClick={onDiscardAndExit}
          onButtonTwoClick={onClickSave}
          setIsDialogOpen={true}
          onClose={closeDialog}
          testId="routine-templates-createroutinetemplate-discarddialog"
        />
      ) : null}
      <DSProvider dsVersion="1.0">
        <RightPane
          showRightPane={showRightPane}
          rightPaneData={rightPaneData}
          title={t("journal:field_options")}
          toggleRightPane={toggleRightPane}
          rightPaneStyle={styles.rightPaneContainerStyle}
          rightPaneHeaderStyle={styles.rightPaneHeaderStyle}
          rightPaneMiddleContainerStyle={styles.rightPaneMiddleContainer}
          titleClass="text-heading-6"
          disableCloseOnBackdrop={true}
          crossButtonProps={crossButtonProps}
          testId="routine-templates-createroutinetemplate-rightpane"
        />
      </DSProvider>
      {showFullScreenLoader ? (
        <FullScreenLoader testId="routine-templates-createroutinetemplate-fullscreenloader" />
      ) : null}
    </DSProvider>
  );
};

const mapStateToProps = state => {
  const mappedClasses = state.routineTemplates.mappings.classes;
  const isAllClassesSelected = state.routineTemplates.isAllClassesSelected;
  const curriculumProgramId = getSelectedCurriculumId(state);
  return {
    mappedClasses,
    curriculumProgramId,
    isLoading: false,
    isAllClassesSelected,
  };
};

const mapActionCreators = {
  updateClasses,
  deleteRoutineTemplate,
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  CreateRoutineTemplate
);
CreateRoutineTemplate.displayName = "CreateRoutineTemplate";
